import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  withSeparator?: boolean;
}

interface MezzaSelectFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  className?: string;
  onValueChange?: (value: string) => void;
  labelClassName?: string;
  triggerClassName?: string;
  fullWidthOptions?: boolean;
}

/**
 * A reusable select field component that wraps shadcn's Select with form handling
 */
export function MezzaSelectField<T extends FieldValues>({
  form,
  name,
  label,
  placeholder,
  options,
  disabled = false,
  className,
  onValueChange,
  labelClassName,
  triggerClassName,
  fullWidthOptions = false,
}: MezzaSelectFieldProps<T>) {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("w-full", className)}>
          <FormLabel className={labelClassName}>{t(label)}</FormLabel>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              if (onValueChange) {
                onValueChange(value);
              }
            }}
            value={field.value}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger
                className={cn(
                  triggerClassName,
                  fullWidthOptions && "w-full",
                  disabled && "[&>svg]:hidden"
                )}
              >
                <SelectValue
                  placeholder={placeholder ? t(placeholder) : undefined}
                />
              </SelectTrigger>
            </FormControl>

            <SelectContent className="w-[var(--radix-select-trigger-width)]">
              {options.map((option) => (
                <>
                  {option.withSeparator && <SelectSeparator />}

                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className={fullWidthOptions ? "full-width-option" : ""}
                  >
                    <div
                      className={cn(
                        "flex justify-between items-center gap-2",
                        fullWidthOptions && "w-full"
                      )}
                    >
                      <span>{t(option.label)}</span>

                      {option.description && (
                        <span className="text-xs ml-auto text-right">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                </>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
} 